import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { ThemeEvents } from '@theme/events';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogComponent } from '@theme/dialog';

class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];
  #controller = new AbortController();
  #activeFetch = null;
  #resizeObserver = new ResizeObserver(entries => {
    entries.forEach(entry => {
      this.style.setProperty(
        '--predictive-search-results-height',
        `${entry.contentRect.height}px`
      );
    });
  });

  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    if (this.refs.searchInput.value.length > 0) this.#showResetButton();

    if (this.dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      this.dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      this.addEventListener('click', this.#handleModalClick, { signal });
    } else {
      document.addEventListener(ThemeEvents.megaMenuHover, this.#blurSearch, { signal });
    }

    this.#getRecentlyViewed();

    const child = this.refs.predictiveSearchResults.firstElementChild;
    if (child) this.#resizeObserver.observe(child);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#resizeObserver.disconnect();
  }

  #handleModalClick = event => {
    const target = /** @type HTMLElement */ (event.target);
    const interactive = 
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button, a, input');
    if (!interactive) this.refs.searchInput.focus();
  };

  #handleKeyboardShortcut = e => {
    if (e.metaKey && e.key === 'k') this.dialog?.toggleDialog();
  };

  #handleDialogClose = () => {
    this.resetSearch(true);
  };

  expandSearch = () => {
    this.classList.add('predictive-search--expanded');
    if (this.dataset.activeColorScheme) {
      const t = this.dialog ?? this;
      t.classList.add(`color-${this.dataset.activeColorScheme}`);
    }
    document.addEventListener('click', this.handleOutsideClick);
  };

  handleOutsideClick = e => {
    if (!this.contains(e.target)) {
      this.classList.remove('predictive-search--expanded');
      if (this.dataset.activeColorScheme) {
        const t = this.dialog ?? this;
        t.classList.remove(`color-${this.dataset.activeColorScheme}`);
      }
      document.removeEventListener('click', this.handleOutsideClick);
    }
  };

  get #allResultsItems() {
    const containers = Array.from(this.querySelectorAll(
      '.predictive-search-results__wrapper-queries,' +
      '.predictive-search-results__wrapper-products,' +
      '.predictive-search-results__list'
    ));
    return containers
      .flatMap(c => Array.from(c.querySelectorAll('.predictive-search-results__card, [ref="resultsItems[]"]')))
      .filter(i => i instanceof HTMLElement);
  }

  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems.findIndex(i => i.getAttribute('aria-selected') === 'true');
  }

  set #currentIndex(idx) {
    const items = this.#allResultsItems;
    if (!items.length) return;

    items.forEach(i => {
      i.classList.remove('keyboard-focus');
      i.removeAttribute('aria-selected');
    });

    const selected = items[idx];
    if (selected) {
      selected.setAttribute('aria-selected', 'true');
      if (this.#isKeyboardNavigation) {
        selected.classList.add('keyboard-focus');
      }
      selected.scrollIntoView({
        behavior: prefersReducedMotion() ? 'instant' : 'smooth',
        block: 'nearest'
      });
    }

    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems[this.#currentIndex];
  }

  onSearchKeyDown = e => {
    if (e.key === 'Escape') {
      this.resetSearch();
      return;
    }

    const len = this.#allResultsItems.length;
    if (!len || ['ArrowLeft', 'ArrowRight'].includes(e.key)) return;

    const current = this.#currentIndex;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        this.#isKeyboardNavigation = true;
        this.#currentIndex = current < len - 1 ? current + 1 : 0;
        break;
      case 'ArrowUp':
        e.preventDefault();
        this.#isKeyboardNavigation = true;
        this.#currentIndex = current > 0 ? current - 1 : len - 1;
        break;
      case 'Tab':
        e.preventDefault();
        this.#isKeyboardNavigation = true;
        this.#currentIndex = e.shiftKey
          ? (current > 0 ? current - 1 : len - 1)
          : (current < len - 1 ? current + 1 : 0);
        break;
      case 'Enter':
        const single = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (single?.dataset.singleResultUrl) {
          e.preventDefault();
          window.location.href = single.dataset.singleResultUrl;
          return;
        }
        if (current >= 0) {
          e.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const url = new URL(Theme.routes.search_url, location.origin);
          url.searchParams.set('q', this.refs.searchInput.value);
          window.location.href = url.toString();
        }
        break;
    }
  };

  clearRecentlyViewedProducts(event) {
    event?.stopPropagation?.();

    // 🔧 FORCE CLEAR
    localStorage.removeItem('shopify_recently_viewed_products');
    RecentlyViewed.clearProducts?.();

    // Remove on-screen wrapper
    const wrapper = this.refs.recentlyViewedWrapper;
    if (wrapper) wrapper.remove();

    // 🔁 Force UI refresh
    this.resetSearch(false);
  }

  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) this.refs.searchInput.focus();
    this.#resetSearch();
  }, 100);

  search = debounce(e => {
    if (!e.inputType) return;

    const term = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!term.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(term);
  }, 200);

  #resetScrollPositions() {
    requestAnimationFrame(() => {
      const inner = this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner');
      if (inner) inner.scrollTop = 0;
      const content = this.querySelector('.predictive-search-form__content');
      if (content) content.scrollTop = 0;
    });
  }

  async #getSearchResults(term) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][fields]', 'title,variants.sku,vendor,product_type,variants.title');

    const { predictiveSearchResults } = this.refs;
    const controller = this.#createAbortController();

    try {
      const html = await sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
      if (!html || controller.signal.aborted) return;
      morph(predictiveSearchResults, html);
      this.#resetScrollPositions();
    } catch (err) {
      if (controller.signal.aborted) return;
      throw err;
    }
  }

  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;
    const viewed = RecentlyViewed.getProducts() || [];
    if (!viewed.length) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewed.map(id => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  async #getRecentlyViewed() {
    const { predictiveSearchResults } = this.refs;
    const controller = this.#createAbortController();

    const markup = await this.#getRecentlyViewedProductsMarkup();
    if (!markup || controller.signal.aborted) return;

    const doc = new DOMParser().parseFromString(markup, 'text/html');
    const rvContainer = doc.getElementById('predictive-search-products');
    if (!rvContainer) return;

    Array.from(rvContainer.children).forEach(child => {
      if (child instanceof HTMLElement) child.setAttribute('ref', 'recentlyViewedWrapper');
    });

    const target = predictiveSearchResults.querySelector('#predictive-search-products');
    if (!target) return;

    if (this.refs.recentlyViewedWrapper) {
      this.refs.recentlyViewedWrapper.remove();
    }

    if (controller.signal.aborted) return;
    target.prepend(...Array.from(rvContainer.children));
  }

  async #resetSearch() {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    const controller = this.#createAbortController();
    const emptyMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false);
    if (!emptyMarkup || controller.signal.aborted) return;

    const parsed = new DOMParser()
      .parseFromString(emptyMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsed) throw new Error('Empty section markup missing');

    // 🧼 Always refresh recently viewed section (clear or repopulate)
    const rvMarkup = await this.#getRecentlyViewedProductsMarkup();
    const rvContainer = rvMarkup
      ? new DOMParser().parseFromString(rvMarkup, 'text/html').getElementById('predictive-search-products')
      : null;

    const target = parsed.querySelector('#predictive-search-products');
    if (target) {
      target.innerHTML = '';
      if (rvContainer) {
        Array.from(rvContainer.children).forEach(child => {
          if (child instanceof HTMLElement) child.setAttribute('ref', 'recentlyViewedWrapper');
        });
        target.prepend(...Array.from(rvContainer.children));
      }
    }

    if (controller.signal.aborted) return;
    morph(predictiveSearchResults, parsed);
    this.#resetScrollPositions();
  }

  #hideResetButton() {
    this.refs.resetButton.hidden = true;
  }

  #showResetButton() {
    this.refs.resetButton.hidden = false;
  }

  #createAbortController() {
    const ac = new AbortController();
    if (this.#activeFetch) this.#activeFetch.abort();
    this.#activeFetch = ac;
    return ac;
  }

  #blurSearch = () => {
    this.refs.searchInput.blur();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
